import React, { useEffect, useRef, useState } from "react";

type PropsType = {
    rootid: string;
    slot: string; // Ad slot id
    responsiveYn?: "Y" | "N"; // Y면 sideYn/height 무시(응답형)
    sideYn?: "Y" | "N"; // 사이드레일 여부
    height?: number; // 고정형 초기 높이(응답형이 아니고 사이드레일도 아닐 때)
    marginStyle?: string | number; // 광고가 보일 때만 적용할 margin
    refreshKey: string; // 부모가 주는 리마운트 트리거
};

const CLIENT = "ca-pub-7310201622871206"; // 기존 html의 data-ad-client 값과 동일 :contentReference[oaicite:2]{index=2}

declare global {
    interface Window {
        adsbygoogle?: any[];
        __gadsLoadedOnce?: boolean;
    }
}

function ensureAdsScript() {
    if (typeof window === "undefined") return;
    if (window.__gadsLoadedOnce) return;
    const id = "adsbygoogle-js";
    if (!document.getElementById(id)) {
        const s = document.createElement("script");
        s.id = id;
        s.async = true;
        s.crossOrigin = "anonymous";
        s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT}`;
        document.head.appendChild(s);
    }
    window.__gadsLoadedOnce = true;
}

const GoogleAdsense: React.FC<PropsType> = ({
    rootid,
    slot,
    responsiveYn = "N",
    sideYn = "N",
    height = 0,
    marginStyle = "",
    refreshKey,
}) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const insRef = useRef<HTMLModElement | null>(null);
    const moRef = useRef<MutationObserver | null>(null);
    const [hasAd, setHasAd] = useState(false);

    // 외부 스크립트 1회 로드
    useEffect(() => { ensureAdsScript(); }, []);

    // refreshKey가 바뀔 때마다 ins를 "완전 교체"하고 새로 요청
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;

        // 이전 감시자 해제 + 내용 클리어
        try { moRef.current?.disconnect(); } catch { }
        moRef.current = null;
        wrap.innerHTML = "";
        setHasAd(false);

        // 새 ins 생성
        const ins = document.createElement("ins") as HTMLModElement;
        ins.className = "adsbygoogle";
        ins.setAttribute("data-ad-client", CLIENT);
        ins.setAttribute("data-ad-slot", slot);

        // (기존 스타일/속성 세팅은 유지)
        if (responsiveYn === "Y") {
            ins.style.cssText = "display:block; text-align:center;";
            ins.setAttribute("data-ad-layout", "in-article");
            ins.setAttribute("data-ad-format", "fluid");
        } else {
            if (sideYn === "Y") {
                // 사이드레일 width, heigth 값 가져오기
                const rail = document.querySelector(".siderail") as HTMLElement | null;
                const r = rail?.getBoundingClientRect();
                const w = r?.width ?? 0;
                const h = r?.height ?? 0;
                ins.style.cssText = `display:inline-block;width:${w}px;height:${h}px`;
            } else {
                ins.style.cssText = `display:block;height:${height}px`;
            }
        }

        // DOM에 부착
        wrap.appendChild(ins);
        insRef.current = ins;

        // 로드/미입찰 감지 — data-ad-status 변화 감시(기존 html의 로직 이식) :contentReference[oaicite:4]{index=4}
        const check = () => {
            const st = ins.getAttribute("data-ad-status");
            if (st === "filled") {
                setHasAd(true);
                stop();
            } else if (st === "unfilled" || st === "collapsed" || st === "error") {
                setHasAd(false);
                // 공간 접기
                ins.style.height = "0px";
                ins.style.minHeight = "0px";
                stop();
            }
        };
        const mo = new MutationObserver(check);
        mo.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
        moRef.current = mo;
        const poll = window.setInterval(check, 300);
        const stop = () => { try { mo.disconnect(); } catch { } window.clearInterval(poll); };
        // 안전 타임아웃
        const kill = window.setTimeout(stop, 8000);

        // 실제 요청
        try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch { }

        return () => { stop(); window.clearTimeout(kill); };
    }, [refreshKey, slot, responsiveYn, sideYn, height]);

    // 래퍼 스타일 (기존 object의 maxHeight/overflow/margin 반영) :contentReference[oaicite:5]{index=5}
    const wrapperStyle: React.CSSProperties = {
        width: "100%",
        overflow: "hidden",
        margin: hasAd ? (marginStyle || 0) : 0,
        ...(height > 0 ? { maxHeight: height + 10 } : {}), // 얇은 여유
    };

    if (window.location.hostname === "localhost") return null;

    return <div id={rootid} ref={wrapRef} style={wrapperStyle} key={refreshKey} />;
};

// 부모 refreshKey가 같으면 리렌더 스킵(기존 최적화 유지) :contentReference[oaicite:6]{index=6}
export default React.memo(GoogleAdsense, (prev, next) => prev.refreshKey === next.refreshKey);
